import React, { useState, useEffect } from 'react';
import axios from 'axios';

const api = () => axios.create({ baseURL: '/api', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }});

export default function CompliancePage({ user }) {
  const http = api();
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get('/legal/compliance-status').then(r => { setCompliance(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const userData = user || JSON.parse(localStorage.getItem('user') || '{}');

  if (loading) return <div className="min-h-screen bg-neutral-50 flex items-center justify-center"><div className="text-neutral-400">Loading...</div></div>;

  const score = compliance?.compliance_score || 0;
  const scoreColor = score >= 80 ? 'text-brand-600' : score >= 50 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = score >= 80 ? 'bg-brand-50 border-brand-200' : score >= 50 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200';

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <img src="/logo.svg" alt="ShiftMatch" className="w-10 h-10 mx-auto mb-3" onError={e => { e.target.style.display='none'; }} />
          <h1 className="text-2xl font-bold text-neutral-900">Compliance Status</h1>
          <p className="text-neutral-500 mt-1 text-sm">Complete all required items to unlock full platform access</p>
        </div>

        {/* Score card */}
        <div className={`p-6 rounded-2xl border mb-6 text-center ${scoreBg}`}>
          <div className={`text-5xl font-black mb-1 ${scoreColor}`}>{score}%</div>
          <div className="text-neutral-600 font-medium">Compliance Score</div>
          <div className="text-sm text-neutral-500 mt-1">{compliance?.complete}/{compliance?.total} items complete</div>
          {compliance?.required_remaining > 0 && (
            <div className="mt-3 text-sm text-red-600 font-medium">⚠️ {compliance.required_remaining} required item{compliance.required_remaining !== 1 ? 's' : ''} outstanding</div>
          )}
        </div>

        {/* Checklist */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 overflow-hidden mb-6">
          <div className="p-4 border-b border-neutral-100">
            <h2 className="font-bold text-neutral-900">Your Checklist</h2>
          </div>
          <div className="divide-y divide-gray-50">
            {compliance?.checks.map(item => (
              <a key={item.item} href={item.action}
                className="flex items-center gap-4 p-4 hover:bg-neutral-50 transition">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  item.status === 'complete' ? 'bg-brand-100 text-brand-600' :
                  item.status === 'required' ? 'bg-red-100 text-red-600' :
                  'bg-amber-100 text-amber-600'
                }`}>
                  {item.status === 'complete' ? '✓' : item.status === 'required' ? '!' : '○'}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-neutral-900 text-sm">{item.item}</p>
                  <p className={`text-xs mt-0.5 ${
                    item.status === 'complete' ? 'text-brand-600' :
                    item.status === 'required' ? 'text-red-500' : 'text-amber-600'
                  }`}>
                    {item.status === 'complete' ? 'Complete ✓' : item.status === 'required' ? 'Required — tap to complete' : 'Recommended'}
                  </p>
                </div>
                {item.status !== 'complete' && <span className="text-neutral-400 text-sm">→</span>}
              </a>
            ))}
          </div>
        </div>

        {/* Legal info */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-100 p-5">
          <h3 className="font-bold text-neutral-900 mb-3 text-sm">Australian Legal Compliance</h3>
          <div className="space-y-2 text-xs text-neutral-500">
            <p>✓ Privacy Act 1988 (Cth) — Australian Privacy Principles compliant</p>
            <p>✓ Fair Work Act 2009 (Cth) — Casual employment information statement</p>
            <p>✓ Work Health & Safety Act 2011 (Cth) — Duty of care obligations</p>
            <p>✓ A New Tax System (Goods and Services Tax) Act 1999 — GST on platform fees</p>
            {userData.role === 'employer' && <p>✓ Labour Hire Licensing — state licensing requirements vary</p>}
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-100 flex gap-4 text-xs">
            <a href="/terms.html" target="_blank" className="text-brand-600 hover:underline">Terms of Service</a>
            <a href="/privacy.html" target="_blank" className="text-brand-600 hover:underline">Privacy Policy</a>
            <a href="mailto:legal@shiftmatch.work" className="text-brand-600 hover:underline">Legal Contact</a>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a href={userData.role === 'employer' ? '/employer' : '/worker'} className="text-sm text-brand-600 hover:underline">← Back to Dashboard</a>
        </div>
      </div>
    </div>
  );
}
